import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";
import CircleTangentLengthDiagram from "../../../../components/diagrams/CircleTangentLengthDiagram";

const DEC_TOL = 0.01; // jak w Heronie – akceptuj przybliżenia do setnych

/** uproszczenie sqrt(n) → a√b */
function simplifySqrt(n) {
  if (n <= 0) return { outside: 0, inside: 0 };
  let outside = 1, inside = Math.trunc(n);
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      outside *= i;
      inside /= i * i;
    }
  }
  return { outside, inside };
}

/** pomocnicze: zakres liczb całkowitych [a..b] */
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

/** Generator:
 *  - r ∈ {3,…,10}, |OP| ∈ {10,…,30}
 *  - warunki: |OP| > r,  PT = sqrt(|OP|^2 - r^2) ≥ r (czytelny bok),
 *  - P mieści się po prawej w STAŁYM kadrze (width=720, rightReserve=150).
 *  - Zbiera wielu kandydatów i losuje z listy.
 */
export function generateTask() {
  const rPool = range(3, 10);
  const dPool = range(10, 30); // tu „d” to technicznie |OP|

  // parametry rysunku — spójne z renderem wyjaśnienia
  const WIDTH = 720, PADDING = 28, CIRCLE_R = 120, RIGHT_RESERVE = 150;
  const sFor = (r) => CIRCLE_R / r;

  const candidates = [];
  for (const r of rPool) {
    for (const d of dPool) {
      if (!(d > r)) continue;
      const PT = Math.sqrt(d * d - r * r);
      if (!(PT >= r)) continue; // unikamy zbyt krótkiego PT

      // sprawdź, czy P zmieści się po prawej przy stałym kadrze
      const s = sFor(r);
      const PTpx = Math.sqrt(Math.max(0, (d * s) ** 2 - CIRCLE_R ** 2));
      const maxCx = WIDTH - PADDING - 18 /*mR*/ - RIGHT_RESERVE - PTpx;
      const minCx = PADDING + 18 /*mL*/ + CIRCLE_R;
      if (maxCx >= minCx) candidates.push({ r, d, PT });
    }
  }

  if (candidates.length === 0) {
    const r = 10, d = 25;
    return { type: "FindTangentLengthTask", data: { r, d, PT: Math.sqrt(d * d - r * r) } };
  }

  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  return { type: "FindTangentLengthTask", data: pick(candidates) };
}

/* ===== treść (bez rysunku, bez litery d) ===== */
export function renderTask({ data }) {
  const { r, d } = data; // „d” to długość |OP|
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      Z punktu zewnętrznego <InlineMath math={"P"} /> poprowadzono styczną w punkcie <InlineMath math={"T"} /> do okręgu
      o środku <InlineMath math={"O"} /> i promieniu <InlineMath math={"r"} />.
      Wiadomo, że <InlineMath math={`\\;r=${r}\\;\\text{ oraz }\\;|OP|=${d}`} />.
      Oblicz <strong>długość odcinka</strong> <InlineMath math={"PT"} />.
    </div>
  );
}

/* ===== input (z pierwiastkiem jak w Heronie) ===== */
export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="|PT|"
      placeholder="np. (3√5)/2, 13, 7 1/2"
    />
  );
}

/* ===== check (akceptuje postać dokładną i przybliżenie do 0.01) ===== */
export function checkAnswer({ data }, userAnswer) {
  const expected = Math.sqrt(data.d * data.d - data.r * data.r);

  // 1) standardowy parse z RootFractionInput
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: zwykła liczba (z przecinkiem)
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  return Math.abs(parsed - expected) <= DEC_TOL;
}

/* ===== answer — krótko, tylko uproszczony pierwiastek ===== */
export function renderAnswer({ data }) {
  const { r, d } = data;
  const rad = d * d - r * r;
  const { outside, inside } = simplifySqrt(rad);

  let expr;
  if (inside === 1) expr = `${outside}`;
  else if (outside === 1) expr = `\\sqrt{${inside}}`;
  else expr = `${outside}\\sqrt{${inside}}`;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`|PT| = ${expr}`} />
    </div>
  );
}

/* ===== explanation (osobne linie i końcowe uproszczenie) ===== */
export function renderExplanation({ data }) {
  const { r, d } = data;          // d = |OP|
  const d2 = d * d;
  const r2 = r * r;
  const rad = d2 - r2;

  function simplifySqrtLocal(n) {
    if (n <= 0) return { outside: 0, inside: 0 };
    let outside = 1, inside = Math.trunc(n);
    for (let i = 2; i * i <= inside; i++) {
      while (inside % (i * i) === 0) { outside *= i; inside /= i * i; }
    }
    return { outside, inside };
  }
  const { outside, inside } = simplifySqrtLocal(rad);
  const simpl =
    inside === 1 ? `${outside}` :
    outside === 1 ? `\\sqrt{${inside}}` :
    `${outside}\\sqrt{${inside}}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Promień jest prostopadły do stycznej w punkcie styczności,
        więc trójkąt <InlineMath math={"OPT"} /> jest prostokątny
        (<InlineMath math={"\\angle OTP=90^\\circ"} />).
      </div>

      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <CircleTangentLengthDiagram
          r={r}
          d={d}
          width={720}
          height={300}
          padding={28}
          circleR={120}
          rightReserve={150}
          topGap={8}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <strong>Krok 2.</strong> Z twierdzenia Pitagorasa w trójkącie <InlineMath math={"OPT"} />:
      </div>
      <BlockMath math={"|OP|^{2} = r^{2} + |PT|^{2}"} />
      <BlockMath math={"|PT|^{2} = |OP|^{2} - r^{2}"} />

      <BlockMath math={"|PT| = \\sqrt{|OP|^{2} - r^{2}}"} />
      <BlockMath math={`|PT| = \\sqrt{${d}^{2} - ${r}^{2}} = \\sqrt{${d2} - ${r2}}`} />
      <BlockMath math={`|PT| = \\sqrt{${rad}} = ${simpl}`} />
    </div>
  );
}

/* ===== user answer preview (ładny LaTeX jak w Heronie) ===== */
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={formatLatexPreview(raw, "|PT|")} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
